import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelection } from '@cube3/state/src/redux/components/useSelection';
import { useModalActions } from '@cube3/state/src/redux/Hooks/useModalActions';

import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import Button from '@cube3/ui/src/Buttons/Button';
import {
  ExportMultiAssetsInfo,
  ExportSingleAssetInfo
} from '../prefabs/ExportAssetsInfo';
import { Divider } from '@cube3/ui/src/Surface/Divider';
import { useExportOptions } from '../hooks/useExportOptions';
import { Padding } from '../../Contracts/prefabs/Padding';
import { useExportTargetDefaults } from '../../AdminCenter/views/WorkspaceIntegrationsView/hooks/useExportTargetDefaults';
import { useDispatch } from 'react-redux';
import { queueTabs, toggleMediaqueue } from '@cube3/state/src/redux/ducks/ui';
import {
  YouTubeParameters,
  YoutubePlaylist
} from '@cube3/common/model/resource-types';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';
import { useGenericFormState } from '@cube3/forms/src/hooks/useGenericFormState';
import { GenericForm } from '@cube3/forms/src/forms/genericForm/GenericForm';
import {
  objectFromValues,
  objectToFormValues
} from '@cube3/forms/src/utils/objectToFormValues';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';
import { DisabledTitle } from '../prefabs/DisabledTitle';
import { validEmailaddress } from '@cube3/forms/src/utils/validators';

interface Props {
  modalContext: {
    channel: {
      id: string;
      display_name: string;
      display_image?: string;
    };
    exportTargetId: string;
    exportTargetTypeId: string;
    categories?: { id: string; display_name: string }[];
    playlists?: YoutubePlaylist[];
  };
}
export const visibilities = ['Private', 'Unlisted', 'Public'];

export const YoutubeExportOptionsModal: React.FC<Props> = (props) => {
  const { channel, exportTargetId, exportTargetTypeId, playlists } =
    props.modalContext;

  const { previousModal, closeAllModals, goBackModals } = useModalActions();
  const { selection } = useSelection();

  const { createExportStatus, handleCreateExport, exportBatchId } =
    useExportOptions<YouTubeParameters>({
      selection,
      exportTargetId
    });

  const [defaults, defaultsStatus] =
    useExportTargetDefaults(exportTargetTypeId);

  const fields = useMemo(
    () =>
      [
        selection?.length === 1
          ? {
              id: 'title',
              type: 'text',
              label: 'Title',
              placeholder: 'Enter title',
              required: true,
              fullWidth: true
            }
          : {
              id: 'title',
              type: 'custom',
              label: 'Title',
              disabled: false,
              title:
                'You cannot change the title when exporting multiple videos at once. Export the video one by one if you need a different title.',
              component: DisabledTitle
            },
        {
          id: 'description',
          type: 'text',
          label: 'Description',
          placeholder: 'Enter description',
          fullWidth: true
        },
        {
          id: 'visibility',
          type: 'radio',
          label: 'Visibility',
          options: visibilities.map((v) => ({
            value: v.toLowerCase(),
            display_name: v
          }))
        },
        {
          id: 'tags',
          type: 'multiselect',
          label: 'Tags',
          placeholder: 'Enter tags',
          allowAddingOfNewSuggestions: true
        },
        {
          id: 'playlists',
          type: 'checkbox',
          multiple: true,
          label: 'Playlists',
          options: playlists.map((pl) => ({
            id: pl.id,
            value: pl.display_name
          }))
        },
        { id: 'divider', type: 'divider' },
        {
          id: 'recipients',
          type: 'multiselect',
          label: 'Send export notifications to',
          placeholder: 'Enter email address',
          validate: validEmailaddress
        }
      ] as Field[],
    [selection, playlists]
  );

  const initialValues = useMemo(() => {
    return objectToFormValues({
      ...(defaults || {}),
      title: selection?.length === 1 ? selection[0]['display_name'] : undefined
    });
  }, [selection, defaults]);

  const {
    fields: fieldsWithState,
    values,
    valid
  } = useGenericFormState({
    fields,
    initialValues
  });

  const handleSubmit = useCallback(() => {
    if (!values) return;
    const formValue = objectFromValues(values);
    handleCreateExport({
      recipients: formValue.recipients as string[],
      integration_parameters: {
        title: formValue.title as string,
        description: formValue.description as string,
        channel: channel.id,
        channel_name: channel.display_name,
        tags:
          (formValue.tags as string[]).length > 0
            ? (formValue.tags as string[])
            : undefined,
        category_id: formValue.category_id as string,
        playlists: formValue.playlists as string[],
        visibility: (formValue.visibility as string).toLowerCase()
      }
    });
  }, [handleCreateExport, values, channel]);

  const dispatch = useDispatch();

  // open export status view modal
  useEffect(() => {
    if (exportBatchId && createExportStatus === statuses.SUCCESS) {
      closeAllModals();
      dispatch(toggleMediaqueue(true, queueTabs.EXPORTS));
    }
  }, [channel, createExportStatus, exportBatchId]);

  const handleCancel = useCallback(() => {
    goBackModals(2);
  }, [goBackModals]);

  return (
    <ModalMenuUI
      title="Export to YouTube • Options"
      wide={true}
      multiplyContentSpace={10}
      loading={
        (defaultsStatus && !hasResolved(defaultsStatus)) ||
        (createExportStatus && !hasResolved(createExportStatus))
      }
      onCloseEvent={handleCancel}
      footerLeftComponent={
        <Button
          text={'Previous'}
          onClick={previousModal}
          colorVariant={'ghost2'}
        />
      }
      footerRightComponent={
        <>
          <Button
            text={'Cancel'}
            colorVariant={'ghost2'}
            onClick={handleCancel}
            disabled={false}
          />
          <Button
            disabled={!valid}
            colorVariant={'filled1'}
            text={'Export'}
            onClick={handleSubmit}
          />
        </>
      }
    >
      <Padding>
        {selection?.length > 0 ? (
          <>
            {selection.length > 1 ? (
              <ExportMultiAssetsInfo selection={selection} />
            ) : (
              <ExportSingleAssetInfo selection={selection?.[0]} />
            )}
            <Divider gutter={true} />
            <Padding>
              <GenericForm
                fields={fieldsWithState}
                form={'youtube_export_form'}
              />
            </Padding>
          </>
        ) : null}
      </Padding>
    </ModalMenuUI>
  );
};
